/**
 * Who is speaking when, from each camera's own sound.
 *
 * In a podcast each person usually has a camera (and often a mic) close to
 * them, so a camera's sound is loudest, compared with the others, when its
 * person talks. Every angle's sound becomes a level per 100 ms frame on the
 * set's timeline (see speakingLevels.ts); this file decides, frame by frame,
 * whether one angle's person is clearly talking, several are (crosstalk,
 * laughter), or nobody is.
 *
 * Gain-free comparison. Cameras record at different levels, so levels are
 * never compared directly. For each pair of angles, the difference of their
 * levels while anyone talks clusters in two groups: "closer to the first
 * camera" and "closer to the second". The boundary between the groups is
 * found per pair (two-means on the difference, smoothed over about a second
 * so word-to-word wobble doesn't count), and a frame's score for the pair is
 * how far its difference sits from that boundary, in half-separations. An
 * angle's person is talking when it wins against every other angle.
 *
 * A wide camera, whose mic hears everyone from afar, is never the closest
 * mic for anyone, so it rarely wins: talk from people without a camera of
 * their own lands on it, which is where it belongs.
 *
 * Confidence. When the cameras' levels track each other (one shared mic, or
 * every camera far away), no pair separates by enough, and the result says
 * so: nothing is suggested and no lines are named.
 */

import type { PcmClip } from './audioDecode';
import { logEnergyFrames } from './audioSync';
import {
  ActivityReason,
  ActivityRun,
  NO_SOUND_LABEL,
  SEVERAL_LABEL,
  SILENCE_LABEL,
  encodeRuns,
} from '../speakerActivity';

export const SPEAKING_FRAME_S = 0.1;

/** log10 power at or below this is digital silence (a muted or missing track), not room tone. */
const DIGITAL_SILENCE_LOG = -9;

/**
 * Level in dB (10·log10 of the mean power) of each `frameS` frame of a clip,
 * from its first sample; NaN where the sound is digital silence.
 */
export const frameLevelsDb = (clip: PcmClip, frameS = SPEAKING_FRAME_S): Float32Array => {
  const energy = logEnergyFrames(clip, frameS, frameS);
  const out = new Float32Array(energy.length);
  for (let i = 0; i < energy.length; i += 1) out[i] = energy[i] <= DIGITAL_SILENCE_LOG ? NaN : 10 * energy[i];
  return out;
};

export interface AngleLevels {
  key: string;
  /** dB per timeline frame; NaN where the angle has no sound (outside its footage, or unreadable). */
  levels: Float32Array;
}

export interface SpeakingAnalysis {
  frameS: number;
  frames: number;
  /** Angles with enough sound to compare, in the order given. */
  keys: string[];
  runs: ActivityRun[];
  confident: boolean;
  reason: ActivityReason | null;
  /** Seconds with someone talking, and with one clear speaker. */
  speechS: number;
  clearS: number;
}

// ---------------------------------------------------------------------------
// Tuning (seconds and dB)
// ---------------------------------------------------------------------------

/** An angle needs this much sound to take part. */
const MIN_ANGLE_SOUND_S = 10;
/** ...and this much between its quiet and loud moments. */
const MIN_ANGLE_RANGE_DB = 6;
/** A pair needs this much talk in common to be compared. */
const MIN_PAIR_SPEECH_S = 4;
/** Two angles tell people apart when their difference moves by at least this much between speakers. */
const MIN_SEPARATION_DB = 4.5;
/** ...and the smaller group holds at least this share of the talk (otherwise it's stray noise). */
const MIN_MINORITY_SHARE = 0.01;
/** Score (in half-separations) to become the speaker, and to stay the speaker. */
const ENTER_SCORE = 0.5;
const STAY_SCORE = 0.15;
/** Shortest stretches kept; shorter ones join their neighbours. */
const MIN_SILENCE_S = 0.5;
const MIN_SEVERAL_S = 0.4;
const MIN_SPEAKER_S = 0.3;
/** Below this much talk there is too little to go on. */
const MIN_SPEECH_S = 20;
/** At least this share of the talk must have one clear speaker. */
const MIN_CLEAR_SHARE = 0.3;

// Frame codes: 0 no sound, 1 silence, 2 several, 3 + i angle i.
const NONE = 0;
const SILENCE = 1;
const SEVERAL = 2;
const ANGLE = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const quantile = (sorted: Float32Array | number[], q: number): number =>
  sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))))];

const finiteSorted = (values: Float32Array): Float32Array => {
  let count = 0;
  for (let i = 0; i < values.length; i += 1) if (Number.isFinite(values[i])) count += 1;
  const out = new Float32Array(count);
  let at = 0;
  for (let i = 0; i < values.length; i += 1) if (Number.isFinite(values[i])) out[at++] = values[i];
  return out.sort();
};

/** Median of the finite values among `values[from..to)`; NaN when there are none. */
const medianOf = (values: Float32Array, from: number, to: number, scratch: number[]): number => {
  scratch.length = 0;
  for (let i = Math.max(0, from); i < Math.min(values.length, to); i += 1) {
    if (Number.isFinite(values[i])) scratch.push(values[i]);
  }
  if (scratch.length === 0) return NaN;
  scratch.sort((a, b) => a - b);
  const middle = scratch.length >> 1;
  return scratch.length % 2 ? scratch[middle] : (scratch[middle - 1] + scratch[middle]) / 2;
};

/** A centered running median over `width` frames, NaN where the frame itself has no sound. */
const runningMedian = (values: Float32Array, width: number): Float32Array => {
  const out = new Float32Array(values.length);
  const half = width >> 1;
  const scratch: number[] = [];
  for (let i = 0; i < values.length; i += 1) {
    out[i] = Number.isFinite(values[i]) ? medianOf(values, i - half, i + half + 1, scratch) : NaN;
  }
  return out;
};

interface Split {
  boundary: number;
  separation: number;
  minorityShare: number;
}

/** Two-means on sorted values: the boundary halfway between the two groups' means. */
const twoMeans = (sorted: Float32Array): Split | null => {
  const n = sorted.length;
  if (n < 2) return null;
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i += 1) prefix[i + 1] = prefix[i] + sorted[i];
  let low = quantile(sorted, 0.2);
  let high = quantile(sorted, 0.8);
  if (!(high > low)) return { boundary: low, separation: 0, minorityShare: 0 };
  let split = 0;
  for (let step = 0; step < 40; step += 1) {
    const boundary = (low + high) / 2;
    // First index at or above the boundary.
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const middle = (lo + hi) >> 1;
      if (sorted[middle] < boundary) lo = middle + 1;
      else hi = middle;
    }
    if (lo === 0 || lo === n) break;
    const nextLow = prefix[lo] / lo;
    const nextHigh = (prefix[n] - prefix[lo]) / (n - lo);
    split = lo;
    if (Math.abs(nextLow - low) < 1e-4 && Math.abs(nextHigh - high) < 1e-4) break;
    low = nextLow;
    high = nextHigh;
  }
  if (split === 0) return { boundary: (low + high) / 2, separation: 0, minorityShare: 0 };
  return { boundary: (low + high) / 2, separation: high - low, minorityShare: Math.min(split, n - split) / n };
};

interface PairModel {
  usable: boolean;
  boundary: number;
  halfSpan: number;
}

interface Run {
  code: number;
  length: number;
}

const pushRun = (out: Run[], code: number, length: number) => {
  if (length <= 0) return;
  const last = out[out.length - 1];
  if (last && last.code === code) last.length += length;
  else out.push({ code, length });
};

/**
 * Joins stretches shorter than their minimum to their neighbours, shortest
 * first: a stretch between two of the same kind becomes that kind; a short
 * pause between two speakers is split between them; other speech goes to a
 * speaking neighbour, else the longer one. "No sound" is never changed.
 */
export const smoothCodes = (codes: Uint8Array, minFrames: (code: number) => number): Run[] => {
  let runs: Run[] = [];
  for (let i = 0; i < codes.length; i += 1) pushRun(runs, codes[i], 1);
  let longest = 0;
  for (let code = 0; code < ANGLE + 26; code += 1) longest = Math.max(longest, minFrames(code));
  for (let limit = 1; limit < longest; limit += 1) {
    const out: Run[] = [];
    for (let i = 0; i < runs.length; i += 1) {
      const run = runs[i];
      if (run.code === NONE || run.length > limit || run.length >= minFrames(run.code)) {
        pushRun(out, run.code, run.length);
        continue;
      }
      const left = out.length ? out[out.length - 1] : null;
      const right = i + 1 < runs.length ? runs[i + 1] : null;
      const leftOk = Boolean(left && left.code !== NONE);
      const rightOk = Boolean(right && right.code !== NONE);
      if (!leftOk && !rightOk) {
        pushRun(out, run.code, run.length);
        continue;
      }
      if (left && right && leftOk && rightOk && left.code === right.code) {
        left.length += run.length + right.length;
        i += 1;
        continue;
      }
      if (left && right && leftOk && rightOk && run.code === SILENCE) {
        const toLeft = Math.ceil(run.length / 2);
        left.length += toLeft;
        right.length += run.length - toLeft;
        continue;
      }
      let toLeft = leftOk;
      if (left && right && leftOk && rightOk) {
        const leftSpeech = left.code !== SILENCE;
        const rightSpeech = right.code !== SILENCE;
        toLeft = leftSpeech !== rightSpeech ? leftSpeech : left.length >= right.length;
      }
      if (toLeft && left) left.length += run.length;
      else if (right) right.length += run.length;
    }
    runs = out;
  }
  return runs;
};

// ---------------------------------------------------------------------------
// The analysis
// ---------------------------------------------------------------------------

export interface FrameDecisions {
  /** Angles with enough sound to compare. */
  keys: string[];
  /** Per frame: 0 no sound, 1 silence, 2 several, 3 + i the i-th of `keys`. */
  codes: Uint8Array;
  /** Pairs of angles whose sound tells their people apart. */
  usablePairs: number;
}

/**
 * The decision for every frame, before smoothing: who's clearly talking, or
 * several, or silence. `angles` are the angles' levels on the timeline (all
 * the same frame size).
 */
export const classifyFrames = (angles: AngleLevels[], frameS = SPEAKING_FRAME_S): FrameDecisions => {
  let frames = 0;
  angles.forEach((angle) => {
    frames = Math.max(frames, angle.levels.length);
  });

  // 1. Each angle's room tone (floor) and when it hears sound above it.
  const used: Array<{ key: string; levels: Float32Array; floor: number; threshold: number }> = [];
  angles.forEach((angle) => {
    const sorted = finiteSorted(angle.levels);
    if (sorted.length * frameS < MIN_ANGLE_SOUND_S) return;
    const floor = quantile(sorted, 0.1);
    const loud = quantile(sorted, 0.95);
    if (loud - floor < MIN_ANGLE_RANGE_DB) return;
    const threshold = Math.min(15, Math.max(6, 0.33 * (loud - floor)));
    const levels = new Float32Array(frames).fill(NaN);
    levels.set(runningMedian(angle.levels, 3));
    used.push({ key: angle.key, levels, floor, threshold });
  });
  const keys = used.map((angle) => angle.key);
  const count = used.length;

  const sounding = (index: number, frame: number) => {
    const level = used[index].levels[frame];
    return Number.isFinite(level) && level - used[index].floor >= used[index].threshold;
  };
  const active = new Uint8Array(frames);
  for (let k = 0; k < frames; k += 1) {
    for (let i = 0; i < count; i += 1) {
      if (sounding(i, k)) {
        active[k] = 1;
        break;
      }
    }
  }

  // 2. Per pair: the boundary between "closer to i" and "closer to j".
  const pairs: PairModel[][] = [];
  let usablePairs = 0;
  for (let i = 0; i < count; i += 1) {
    pairs.push([]);
    for (let j = 0; j < count; j += 1) pairs[i].push({ usable: false, boundary: 0, halfSpan: 1 });
  }
  for (let i = 0; i < count; i += 1) {
    for (let j = i + 1; j < count; j += 1) {
      const difference = new Float32Array(frames).fill(NaN);
      let shared = 0;
      for (let k = 0; k < frames; k += 1) {
        const a = used[i].levels[k];
        const b = used[j].levels[k];
        if (Number.isFinite(a) && Number.isFinite(b) && (sounding(i, k) || sounding(j, k))) {
          difference[k] = a - b;
          shared += 1;
        }
      }
      if (shared * frameS < MIN_PAIR_SPEECH_S) continue;
      // Over about a second, so a word's wobble doesn't pass for a change of speaker.
      const split = twoMeans(finiteSorted(runningMedian(difference, Math.max(3, Math.round(0.9 / frameS) | 1))));
      if (!split) continue;
      const usable = split.separation >= MIN_SEPARATION_DB && split.minorityShare >= MIN_MINORITY_SHARE;
      if (usable) usablePairs += 1;
      const halfSpan = Math.max(split.separation / 2, 0.5);
      pairs[i][j] = { usable, boundary: split.boundary, halfSpan };
      pairs[j][i] = { usable, boundary: -split.boundary, halfSpan };
    }
  }

  // 3. Frame by frame, with hysteresis: a speaker keeps the floor on a weaker score.
  const codes = new Uint8Array(frames);
  const candidates: number[] = [];
  let holder = -1;
  for (let k = 0; k < frames; k += 1) {
    candidates.length = 0;
    for (let i = 0; i < count; i += 1) if (Number.isFinite(used[i].levels[k])) candidates.push(i);
    if (candidates.length === 0) {
      codes[k] = NONE;
      continue;
    }
    if (!active[k]) {
      codes[k] = SILENCE;
      continue;
    }
    if (candidates.length < 2) {
      codes[k] = SEVERAL;
      holder = -1;
      continue;
    }
    let best = -1;
    let bestScore = -Infinity;
    let holderScore = -Infinity;
    for (let c = 0; c < candidates.length; c += 1) {
      const i = candidates[c];
      let score = Infinity;
      for (let d = 0; d < candidates.length; d += 1) {
        const j = candidates[d];
        if (j === i) continue;
        const pair = pairs[i][j];
        const value = pair.usable
          ? Math.max(-3, Math.min(3, (used[i].levels[k] - used[j].levels[k] - pair.boundary) / pair.halfSpan))
          : 0;
        if (value < score) score = value;
      }
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
      if (i === holder) holderScore = score;
    }
    if (bestScore >= ENTER_SCORE) {
      holder = best;
    } else if (!(holder >= 0 && holderScore >= STAY_SCORE)) {
      holder = -1;
    }
    codes[k] = holder >= 0 ? ANGLE + holder : SEVERAL;
  }
  return { keys, codes, usablePairs };
};

/**
 * Crosstalk at 100 ms looks like quick turns: syllables of two people
 * interleave, so frames flip between them. Where, within about two seconds,
 * the talk flips back and forth (three changes or more) and two people each
 * have a quarter of it, everyone talking there is marked as several. A clean
 * change of speaker (one flip) or a short interjection (too little of the
 * talk) stays as it is.
 */
export const markCrosstalk = (codes: Uint8Array, frameS = SPEAKING_FRAME_S): Uint8Array => {
  const out = codes.slice();
  const radius = Math.max(1, Math.round(1 / frameS));
  const counts: number[] = [];
  for (let k = 0; k < codes.length; k += 1) {
    if (codes[k] < SEVERAL) continue;
    counts.length = 0;
    let talk = 0;
    let flips = 0;
    let previous = -1;
    for (let j = Math.max(0, k - radius); j <= Math.min(codes.length - 1, k + radius); j += 1) {
      const code = codes[j];
      if (code < SEVERAL) continue;
      talk += 1;
      if (code < ANGLE) continue;
      counts[code] = (counts[code] ?? 0) + 1;
      if (previous >= 0 && code !== previous) flips += 1;
      previous = code;
    }
    if (flips < 3) continue;
    let sharing = 0;
    counts.forEach((value) => {
      if (value >= 0.25 * talk) sharing += 1;
    });
    if (sharing >= 2) out[k] = SEVERAL;
  }
  return out;
};

/**
 * Who's speaking in every frame. `angles` are the angles' levels on the
 * timeline (all the same frame size); the result's runs use their keys.
 */
export const analyseSpeaking = (angles: AngleLevels[], frameS = SPEAKING_FRAME_S): SpeakingAnalysis => {
  const { keys, codes: raw, usablePairs } = classifyFrames(angles, frameS);
  const count = keys.length;
  const codes = markCrosstalk(raw, frameS);

  // 4. Short stretches join their neighbours.
  const minFrames = (code: number) => {
    if (code === NONE) return 0;
    if (code === SILENCE) return Math.round(MIN_SILENCE_S / frameS);
    if (code === SEVERAL) return Math.round(MIN_SEVERAL_S / frameS);
    return Math.round(MIN_SPEAKER_S / frameS);
  };
  const smoothed = smoothCodes(codes, minFrames);

  const label = (code: number) => {
    if (code === NONE) return NO_SOUND_LABEL;
    if (code === SILENCE) return SILENCE_LABEL;
    if (code === SEVERAL) return SEVERAL_LABEL;
    return keys[code - ANGLE];
  };
  const runs: ActivityRun[] = [];
  let at = 0;
  let speechFrames = 0;
  let clearFrames = 0;
  smoothed.forEach((run) => {
    runs.push({ label: label(run.code), start: at, length: run.length });
    at += run.length;
    if (run.code >= SEVERAL) speechFrames += run.length;
    if (run.code >= ANGLE) clearFrames += run.length;
  });

  // 5. Could it tell?
  let reason: ActivityReason | null = null;
  if (count < 2) reason = 'one-angle';
  else if (speechFrames * frameS < MIN_SPEECH_S) reason = 'too-little-speech';
  else if (usablePairs === 0 || clearFrames < MIN_CLEAR_SHARE * speechFrames) reason = 'too-alike';

  return {
    frameS,
    frames: raw.length,
    keys,
    runs,
    confident: reason === null,
    reason,
    speechS: Math.round(speechFrames * frameS * 10) / 10,
    clearS: Math.round(clearFrames * frameS * 10) / 10,
  };
};

/** The runs as stored ("S12A40X5"). */
export const encodeAnalysis = (analysis: SpeakingAnalysis): string => encodeRuns(analysis.runs);
